import { FC, useEffect, useState } from "react";
import { useGetCompanySpecificProductById, useGetCompanySpecificServices } from "@/services/Company";
import { useGetCompanyIndexRegulationPercentage, useGetPaymentOptions } from "@/services/Settings";
import { useTranslation } from "react-i18next";
import { Contract, ContractCompanySpecificService, CreateOrderActiveValues, PlatformService } from "@/types";
import { EditContractInput } from "../../validation/editContract";
import { Controller, UseFormReturn } from "react-hook-form";
import {
  calculateBuyerPrice,
  calculateTotalPrice,
  getContractTradeType,
  getTradingPartnerCompany,
} from "@/utils/Contracts";
import { Grid, Group, UnstyledButton } from "@mantine/core";
import Select from "@/components/atoms/Select";
import TextInput from "@/components/atoms/TextInput";
import RadioButtonGroup from "@/components/molecules/RadioButtonGroup";
import Text from "@/components/atoms/Text";
import Stack from "@/components/atoms/Stack";
import Checkbox from "@/components/atoms/Checkbox";
import { useAuth } from "@/contexts/AuthContext";
import ContractEndTypeEnum from "@/constants/enums/ContractEndTypeEnum";
import ContractTradeTypeEnum from "@/constants/enums/ContractTradeTypeEnum";
import InvertPaymentSwitch from "@/components/molecules/SharedContract/InvertPaymentSwitch";
import BuyerAndSellerPriceLabels from "@/components/molecules/SharedContract/BuyerAndSellerPriceLabels";
import PriceTextInputLabel from "@/components/molecules/SharedContract/PriceTextInputLabel";
import NumberFormatter from "@/components/atoms/NumberFormatter";
import Switch from "@/components/atoms/Switch";
import { useGetIndexRegulationPercentage } from "@/services/Contracts";

interface InvoicingProps {
  contract: Contract;
  disableForm: boolean;
  editContractForm: UseFormReturn<EditContractInput>;
  createOrderValues: CreateOrderActiveValues | undefined;
}

const Invoicing = ({ contract, editContractForm, createOrderValues, disableForm }: InvoicingProps) => {
  const { t } = useTranslation();

  const { currentCompany } = useAuth();
  const [totalContractValue, setTotalContractValue] = useState<string>("");
  const [companySpecificServices, setCompanySpecificServices] = useState<ContractCompanySpecificService[]>([]);
  const [editingServiceId, setEditingServiceId] = useState<number | null>(null);

  const contractTradeType = getContractTradeType(contract, currentCompany);
  const tradingPartnerCompany = getTradingPartnerCompany(contract, currentCompany?.companyId);

  const { data: paymentOptions } = useGetPaymentOptions({
    staleTime: 1000 * 60 * 5,
  });
  const { data: companySpecificServicesData } = useGetCompanySpecificServices(contract.createdByCompany, false, {
    staleTime: 1000 * 60 * 5,
  });

  const { data: indexRegulationPercentage } = useGetIndexRegulationPercentage();

  const createdByCompanyIndexRegulationPercentage = contract.contractSellers.find(
    (sellerCompany) => sellerCompany.id === contract.createdByCompany
  )
    ? contract.contractSellers[0].indexRegulationPercentage
    : contract.contractBuyers[0].indexRegulationPercentage;

  const effectiveIndexRegulationPercentage = createdByCompanyIndexRegulationPercentage ?? indexRegulationPercentage;

  useEffect(() => {
    if (companySpecificServicesData && companySpecificServices.length === 0) {
      const mappedServices: ContractCompanySpecificService[] = companySpecificServicesData.map((service) => ({
        companySpecificServiceId: service.id,
        price:
          contract.companySpecificServices.find((s) => s.companySpecificServiceId === service.id)?.price ||
          service.price,
        currencyType: service.currency,
      }));

      setCompanySpecificServices(mappedServices);
    }
  }, [companySpecificServicesData]);

  useEffect(() => {
    if (editContractForm.getValues("contractType") === ContractEndTypeEnum.AMOUNT) {
      const amount = editContractForm.getValues("amount")?.toString();
      const buyerPrice = editContractForm.getValues("agreedBuyerPricePrUnit");
      if (amount && buyerPrice) {
        const newTotalPrice = calculateTotalPrice(buyerPrice.toString(), parseFloat(amount));
        setTotalContractValue(newTotalPrice);
      }
    }
  }, [editContractForm.watch("amount"), editContractForm.watch("agreedBuyerPricePrUnit")]);

  const onChangePlatformServices = (e: any, service: PlatformService) => {
    const currentSelectedServices = e.target.checked
      ? [...editContractForm.getValues("selectedServices"), service.id]
      : editContractForm.getValues("selectedServices").filter((s: number) => s !== service.id);

    editContractForm.setValue("selectedServices", currentSelectedServices);

    const priceWeightUnit = createOrderValues?.units.find(
      (unit) => unit.id === editContractForm.getValues("priceUnitId")
    )?.id;
    if (priceWeightUnit === undefined) return;
    if (companySpecificProduct === undefined) return;

    const invoiceDueDateId = editContractForm.getValues("invoiceDueDateId");
    const dueDateDays = paymentOptions?.dueDates.find((date) => date.id === invoiceDueDateId)?.days;

    const buyPrice = calculateBuyerPrice(
      editContractForm.getValues("agreedSellerPricePrUnit").toString(),
      currentSelectedServices,
      paymentOptions?.settings,
      priceWeightUnit,
      companySpecificProduct,
      editContractForm.watch("isPaymentReceiverInverted"),
      dueDateDays
    );

    editContractForm.setValue("agreedBuyerPricePrUnit", parseFloat(buyPrice));

    const amount = editContractForm.getValues("amount");
    if (amount === null) return;
    const newTotalPrice = parseFloat(buyPrice) * parseFloat(amount);
    setTotalContractValue(newTotalPrice.toFixed(2).toString());
  };

  const { data: companySpecificProduct } = useGetCompanySpecificProductById(
    editContractForm.watch("companySpecificProductId")
  );

  const onChangeSalesPrice = (value: string) => {
    const priceWeightUnit = createOrderValues?.units.find(
      (unit) => unit.id === editContractForm.getValues("priceUnitId")
    )?.id;
    if (priceWeightUnit === undefined) return;
    if (companySpecificProduct === undefined) return;

    const invoiceDueDateId = editContractForm.getValues("invoiceDueDateId");
    const dueDateDays = paymentOptions?.dueDates.find((date) => date.id === invoiceDueDateId)?.days;

    const newBuyerPrice = calculateBuyerPrice(
      value,
      editContractForm.getValues("selectedServices"),
      paymentOptions?.settings,
      priceWeightUnit,
      companySpecificProduct,
      editContractForm.watch("isPaymentReceiverInverted"),
      dueDateDays
    );
    editContractForm.setValue("agreedBuyerPricePrUnit", parseFloat(newBuyerPrice));

    const amount = editContractForm.getValues("amount");
    if (!amount) return;
    const newTotalPrice = calculateTotalPrice(newBuyerPrice, parseFloat(amount));
    setTotalContractValue(newTotalPrice);
  };

  const onChangeIsPaymentReceiverInverted = (value: boolean) => {
    editContractForm.setValue("isPaymentReceiverInverted", value);
    onChangeSalesPrice(editContractForm.watch("agreedSellerPricePrUnit").toString());
  };

  const onChangePriceUnit = (priceWeightUnitId: number) => {
    if (companySpecificProduct === undefined) return;

    const dueDateDays = paymentOptions?.dueDates.find(
      (date) => date.id === editContractForm.watch("invoiceDueDateId")
    )?.days;

    const buyerPrice = calculateBuyerPrice(
      editContractForm.watch("agreedSellerPricePrUnit").toString(),
      editContractForm.watch("selectedServices"),
      paymentOptions?.settings,
      priceWeightUnitId,
      companySpecificProduct,
      editContractForm.watch("isPaymentReceiverInverted"),
      dueDateDays
    );

    editContractForm.setValue("agreedBuyerPricePrUnit", Number(buyerPrice));
    editContractForm.setValue("agreedSellerPricePrUnit", editContractForm.watch("agreedSellerPricePrUnit"));
  };

  const TotalPrice = () => {
    const weightUnitId = editContractForm.watch("priceUnitId");
    const weightUnit = createOrderValues?.units.find((unit) => unit.id === weightUnitId)?.value;

    return (
      <NumberFormatter
        value={Math.abs(Number(totalContractValue))}
        suffix={` ${editContractForm.watch("currencyType")}/${weightUnit}`}
      />
    );
  };

  return (
    <>
      <Grid>
        {contract.createdByCompany === currentCompany?.companyId ? (
          <Grid.Col span={{ base: 12, xl: 6 }}>
            <Grid>
              <Grid.Col span={12}>
                <Group gap="0" align="top">
                  <Controller
                    control={editContractForm.control}
                    name="agreedSellerPricePrUnit"
                    key="sale-price"
                    render={({ field: { onChange, value }, fieldState: { error } }) => {
                      return (
                        <TextInput
                          id="edit-contract-sell-price"
                          label={
                            <PriceTextInputLabel
                              contractType={contractTradeType}
                              tradingPartnerCompanyName={tradingPartnerCompany ? tradingPartnerCompany.companyName : ""}
                              currentCompanyName={currentCompany?.companyName}
                            />
                          }
                          flex={"0 0 40%"}
                          disabled={disableForm}
                          value={value}
                          onChange={(e) => {
                            onChangeSalesPrice(e.target.value);
                            onChange(e.target.value);
                          }}
                          error={error ? t("validation:" + error?.message) : undefined}
                        />
                      );
                    }}
                  />
                  <Controller
                    control={editContractForm.control}
                    name="currencyType"
                    render={({ field: { onChange }, fieldState: { error } }) => (
                      <Select
                        id={"edit-contract-currency"}
                        label={" "}
                        flex={"0 0 20%"}
                        disabled={true}
                        data={createOrderValues?.currencies.map((currency) => ({
                          value: currency.value,
                          label: currency.value.toUpperCase(),
                        }))}
                        defaultValue={
                          createOrderValues?.currencies.find(
                            (c) => c.value.toUpperCase() === contract.contractProduct.currencyType
                          )?.value
                        }
                        onChange={(e) => (e ? onChange(e.toUpperCase()) : onChange(undefined))}
                        error={error?.message}
                      />
                    )}
                  />
                  <Controller
                    control={editContractForm.control}
                    name="priceUnitId"
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Select
                        id={"edit-contract-price-unit"}
                        label={"Per"}
                        flex={"0 0 20%"}
                        disabled={disableForm}
                        pl={16}
                        data={createOrderValues?.units.map((unit) => ({
                          value: unit.id.toString(),
                          label: t("enumerables:weightUnitsLowercase." + unit.value),
                        }))}
                        defaultValue={value.toString()}
                        onChange={(e) => {
                          if (e) {
                            onChange(Number.parseInt(e));
                          } else {
                            onChange(undefined);
                          }
                          onChangePriceUnit(Number(e));
                        }}
                        error={error?.message}
                      />
                    )}
                  />
                </Group>
              </Grid.Col>

              <Grid.Col span={12}>
                <Controller
                  control={editContractForm.control}
                  name="isPaymentReceiverInverted"
                  render={({ field: { value } }) => (
                    <InvertPaymentSwitch
                      contractType={contractTradeType}
                      tradingPartnerCompanyName={tradingPartnerCompany ? tradingPartnerCompany.companyName : ""}
                      currentCompanyName={currentCompany?.companyName}
                      value={value}
                      onChangeIsPaymentReceiverInverted={onChangeIsPaymentReceiverInverted}
                      disabled={disableForm}
                    />
                  )}
                />
              </Grid.Col>

              <Grid.Col span={12}>
                <Stack>
                  <Text size="sm" color="secondary">
                    {t("common:services")}
                  </Text>
                  <Grid>
                    {paymentOptions &&
                      paymentOptions.settings.map((option) => {
                        return (
                          <Grid.Col key={option.id} span={{ base: 6, lg: 4 }}>
                            <Checkbox
                              id={`edit-contract-service-${option.id}`}
                              disabled={option.name === "servicefee" || disableForm}
                              label={
                                option.name +
                                (contract.createdByCompany === currentCompany.companyId
                                  ? ` (${option.priceOrPercent} ${option.currencyType}/${option.platformServiceUnit.code.toLowerCase()})`
                                  : "")
                              }
                              checked={editContractForm.watch("selectedServices").includes(option.id)}
                              onChange={(e) => onChangePlatformServices(e, option)}
                            />
                          </Grid.Col>
                        );
                      })}
                  </Grid>
                </Stack>
              </Grid.Col>

              <BuyerAndSellerPriceLabels
                contractType={contractTradeType}
                isPaymentReceiverInverted={editContractForm.watch("isPaymentReceiverInverted")}
                agreedBuyerPricePrUnit={String(editContractForm.watch("agreedBuyerPricePrUnit"))}
                agreedSellerPricePrUnit={String(editContractForm.watch("agreedSellerPricePrUnit"))}
                currentCompanyName={currentCompany?.companyName}
                tradingPartnerCompanyName={tradingPartnerCompany ? tradingPartnerCompany.companyName : ""}
                unitOptions={createOrderValues?.units || []}
                priceUnitId={editContractForm.watch("priceUnitId")}
                currencyType={contract.contractProduct.currencyType}
                indexRegulated={editContractForm.watch("indexRegulated")}
                indexRegulationPercentage={effectiveIndexRegulationPercentage || 0}
              />
              {editContractForm.watch("contractType") === ContractEndTypeEnum.AMOUNT && (
                <Grid.Col span={12}>
                  <Stack>
                    <Text size="sm" color="secondary">
                      {`${t("contracts:totalContractValue")}:`}
                    </Text>
                    <Text size="lg">
                      <TotalPrice />
                    </Text>
                  </Stack>
                </Grid.Col>
              )}
            </Grid>
          </Grid.Col>
        ) : (
          <Grid.Col span={{ base: 12, xl: 6 }}>
            <Grid>
              {contractTradeType === ContractTradeTypeEnum.BUY ? (
                <Grid.Col span={6}>
                  <Stack>
                    <Text size="sm" color="secondary">
                      {`${t("contracts:buyPrice")}`}
                    </Text>
                    <Text size="lg">
                      <NumberFormatter
                        value={Math.abs(
                          contract.contractProduct.agreedBuyerPricePrUnit[contract.contractProduct.currencyType]
                        )}
                        suffix={` ${contract.contractProduct.currencyType} per ${t("enumerables:weightUnitsLowercase." + contract.contractProduct.priceUnit.unit)}`}
                      />
                    </Text>
                  </Stack>
                </Grid.Col>
              ) : (
                <Grid.Col span={6}>
                  <Stack>
                    <Text size="sm" color="secondary">
                      {`${t("contracts:sellPrice")}`}
                    </Text>
                    <Text size="lg">
                      <NumberFormatter
                        value={Math.abs(
                          contract.contractProduct.agreedSellerPricePrUnit[contract.contractProduct.currencyType]
                        )}
                        suffix={` ${contract.contractProduct.currencyType} per ${t("enumerables:weightUnitsLowercase." + contract.contractProduct.priceUnit.unit)}`}
                      />
                    </Text>
                  </Stack>
                </Grid.Col>
              )}

              <Grid.Col span={12}>
                <Stack>
                  <Text size="sm" color="secondary">
                    {t("common:services")}
                  </Text>
                  <Grid>
                    {paymentOptions &&
                      paymentOptions.settings.map((option) => {
                        return (
                          <Grid.Col key={option.id} span={{ base: 6, lg: 4 }}>
                            <Checkbox
                              id={`edit-contract-service-${option.id}`}
                              disabled={
                                option.name === "servicefee" || contract.createdByCompany !== currentCompany?.companyId
                              }
                              label={
                                option.name +
                                (contract.createdByCompany === currentCompany?.companyId
                                  ? ` (${option.priceOrPercent} ${option.currencyType}/${option.platformServiceUnit.code.toLowerCase()})`
                                  : "")
                              }
                              checked={editContractForm.watch("selectedServices").includes(option.id)}
                              onChange={(e) => onChangePlatformServices(e, option)}
                            />
                          </Grid.Col>
                        );
                      })}
                  </Grid>
                </Stack>
              </Grid.Col>
            </Grid>
          </Grid.Col>
        )}

        <Grid.Col span={{ base: 12, xl: 6 }}>
          <Grid>
            <Grid.Col span={12}>
              <Grid>
                {companySpecificServices
                  ? companySpecificServices.map((service) => {
                      const _serviceDetails = companySpecificServicesData?.find(
                        (s) => s.id === service.companySpecificServiceId
                      );
                      return (
                        <Grid.Col key={service.companySpecificServiceId} span={{ base: 6, lg: 6 }}>
                          <Stack>
                            <Text size="sm" color="secondary">{`+ ${_serviceDetails?.name}`}</Text>
                            <Group align="center">
                              <Checkbox
                                key={service.companySpecificServiceId}
                                disabled={disableForm}
                                style={{ alignItems: "center" }}
                                label={
                                  editingServiceId === service.companySpecificServiceId ? (
                                    <Group>
                                      <TextInput
                                        id={`edit-contract-service-${service.companySpecificServiceId}`}
                                        style={{
                                          height: "23px",
                                          width: "73px",
                                        }}
                                        size="xs"
                                        value={service.price}
                                        onBlur={() => setEditingServiceId(null)}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") {
                                            setEditingServiceId(null);
                                          }
                                        }}
                                        onChange={(e: any) => {
                                          const newPrice = isNaN(e.target.value) ? 0 : e.target.value;

                                          const newFormArray = editContractForm
                                            .watch("companySpecificServices")
                                            .map((s) =>
                                              s.companySpecificServiceId === service.companySpecificServiceId
                                                ? { ...s, price: newPrice }
                                                : s
                                            );
                                          editContractForm.setValue("companySpecificServices", newFormArray);

                                          setCompanySpecificServices((prev) =>
                                            prev.map((s) =>
                                              s.companySpecificServiceId === service.companySpecificServiceId
                                                ? { ...s, price: newPrice }
                                                : s
                                            )
                                          );
                                        }}
                                      />
                                      <Text size="sm">
                                        {_serviceDetails?.currency}/
                                        {t(
                                          "enumerables:paymentIntervals." + _serviceDetails?.priceUnit.code
                                        ).toLowerCase()}
                                      </Text>
                                    </Group>
                                  ) : (
                                    <NumberFormatter
                                      value={service.price}
                                      suffix={` ${_serviceDetails?.currency}/
                                        ${t(
                                          "enumerables:paymentIntervals." + _serviceDetails?.priceUnit.code
                                        ).toLowerCase()}`}
                                    />
                                  )
                                }
                                checked={editContractForm
                                  .watch("companySpecificServices")
                                  .some((s) => s.companySpecificServiceId === service.companySpecificServiceId)}
                                onChange={(e: any) => {
                                  const checkedService = companySpecificServices.find(
                                    (s) => s.companySpecificServiceId === service.companySpecificServiceId
                                  );
                                  editContractForm.setValue(
                                    "companySpecificServices",
                                    e.target.checked && checkedService
                                      ? [...editContractForm.watch("companySpecificServices"), checkedService]
                                      : editContractForm
                                          .watch("companySpecificServices")
                                          .filter(
                                            (s) =>
                                              s.companySpecificServiceId !== checkedService?.companySpecificServiceId
                                          )
                                  );
                                }}
                              />

                              {contract.createdByCompany === currentCompany?.companyId && (
                                <>
                                  {editingServiceId !== service.companySpecificServiceId ? (
                                    <TextCTA
                                      onClick={() =>
                                        setEditingServiceId(
                                          editingServiceId === service.companySpecificServiceId
                                            ? null
                                            : service.companySpecificServiceId
                                        )
                                      }
                                      text={`(${t("actions:change")})`}
                                    />
                                  ) : null}{" "}
                                </>
                              )}
                            </Group>
                          </Stack>
                        </Grid.Col>
                      );
                    })
                  : null}
              </Grid>
            </Grid.Col>

            <Grid.Col span={12}>
              <Group align="center" wrap="nowrap">
                <Controller
                  control={editContractForm.control}
                  name="invoiceDueDateId"
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Select
                      id={"edit-contract-due-date"}
                      flex={"0 0 50%"}
                      label={t("contracts:due")}
                      disabled={disableForm}
                      data={
                        paymentOptions?.dueDates
                          .sort((a, b) => a.days - b.days)
                          .map((date) => ({
                            label: date.days.toString(),
                            value: date.id.toString(),
                          })) || []
                      }
                      value={value.toString()}
                      onChange={(e) => {
                        onChange(Number.parseInt(e ? e : "0"));
                        onChangeSalesPrice(editContractForm.watch("agreedSellerPricePrUnit").toString());
                      }}
                      error={error?.message}
                    />
                  )}
                />
                <Text size="sm" color="secondary" flex={"0 0 50%"}>
                  {t("contracts:dueExplanation")}
                </Text>
              </Group>
            </Grid.Col>

            <Grid.Col span={12}>
              <Stack>
                <Text size="sm" color="secondary">
                  {t("contracts:billingFrequency")}
                </Text>
                {paymentOptions && (
                  <Controller
                    control={editContractForm.control}
                    name="invoiceIntervalId"
                    render={({ field: { onChange, value } }) => (
                      <RadioButtonGroup
                        id={"edit-contract-invoice-interval"}
                        name={"invoiceIntervalId"}
                        disabled={disableForm}
                        options={paymentOptions?.intervals.map((interval) => ({
                          label: t(`enumerables:invoicePaymentIntervals.${interval.code}`),
                          value: interval.id.toString(),
                        }))}
                        defaultValue={
                          paymentOptions?.intervals.find((interval) => interval.id === value)?.id.toString() || ""
                        }
                        onValueChange={(value: any) => {
                          onChange(Number.parseInt(value));
                        }}
                      />
                    )}
                  />
                )}
              </Stack>
            </Grid.Col>
            <Grid.Col span={12}>
              <Controller
                control={editContractForm.control}
                name="skipInvoicing"
                render={({ field: { value, onChange } }) => {
                  return (
                    <Switch
                      label={t("contracts:skipInvoicing")}
                      defaultChecked={contract.skipInvoicing}
                      checked={value}
                      disabled={disableForm}
                      onChange={(e) => onChange(e.currentTarget.checked)}
                    />
                  );
                }}
              />
            </Grid.Col>

            <Grid.Col span={12}>
              <Controller
                control={editContractForm.control}
                name="indexRegulated"
                render={({ field: { value, onChange } }) => {
                  return (
                    <Switch
                      label={t("contracts:indexRegulated")}
                      defaultChecked={contract.indexRegulated}
                      checked={value}
                      disabled={disableForm}
                      onChange={(e) => onChange(e.currentTarget.checked)}
                    />
                  );
                }}
              />
            </Grid.Col>
          </Grid>
        </Grid.Col>
      </Grid>
    </>
  );
};

export default Invoicing;

interface TextCTAProps {
  text: string;
  onClick?: () => void;
}
const TextCTA: FC<TextCTAProps> = ({ text, onClick }) => {
  return (
    <UnstyledButton
      onClick={onClick}
      style={{
        color: "var(--color-text-secondary)",
        fontSize: "var(--text-size-14)",
      }}
    >
      {text}
    </UnstyledButton>
  );
};
